import type { Me, Role, UserStatus } from './me';
import { ORIGINS, LOGIN_PATH, VERIFY_EMAIL_PATH } from './origins';

export { LOGIN_PATH, VERIFY_EMAIL_PATH };

/**
 * The status/role routing rules from docs/06 §3, in one table so the three SPAs cannot drift.
 *
 * Since D16 the four frontends are separate ORIGINS, not paths on one host: the landing
 * (and therefore /login, /register, /verify-email) is the bare domain, and each SPA has its
 * own subdomain and is served from the root of it. So a cross-app redirect is an absolute
 * URL and always a full-page navigation — a React Router push cannot leave the origin.
 *
 * The origins are injected at BUILD time (see ./origins), because the same source builds
 * against localhost ports in dev and mostholding.org in production.
 */
export const APP_ROOTS: Record<Role, string> = {
  buyer: ORIGINS.buyer,
  producer: ORIGINS.producer,
  admin: ORIGINS.admin,
};

/** Where a logged-in user of this role belongs. Absolute — it is another origin. */
export function roleHome(role: Role): string {
  return APP_ROOTS[role];
}

export type GuardDecision =
  | { kind: 'allow' }
  /** Full-page navigation — the target may live in a different frontend build. */
  | { kind: 'redirect'; to: string; external: boolean }
  /** In-app 403 screen. Never leaks whether the route exists. */
  | { kind: 'forbidden' }
  /** Neutral "account unavailable, contact MOST" screen. */
  | { kind: 'blocked' }
  /** Force the temporary-password change before anything else (admin-created accounts). */
  | { kind: 'must-change-password' };

/**
 * The whole of docs/06 §3, evaluated in the order the table is written. Order matters:
 * a blocked user must not be routed to their role home first and bounced afterwards.
 */
export function evaluateGuard(
  me: Me | null | undefined,
  requiredRole: Role,
): GuardDecision {
  // Unauthenticated → the landing's login page, on another origin.
  //
  // No `?next=`. It used to carry the in-SPA path back, but the login page never read it —
  // it routes by role — so it was already dead. Reviving it across origins would mean the
  // landing taking a redirect target from a query string, which is an open redirect unless
  // very carefully validated. Role-based routing needs no such parameter. (The one thing that
  // does ride along is a category SLUG — see ./intent — which is not a target.)
  if (!me) {
    return { kind: 'redirect', to: LOGIN_PATH, external: true };
  }

  // Session was already invalidated server-side; this is only the copy the SPA still holds.
  if (me.status === 'blocked') return { kind: 'blocked' };

  // Only the verification prompt is reachable.
  if (me.status === 'pending_email') {
    return { kind: 'redirect', to: VERIFY_EMAIL_PATH, external: true };
  }

  // Admin-created accounts carry a temporary password and must change it at first login.
  if (me.must_change_password) return { kind: 'must-change-password' };

  // Role mismatch → in-app 403, NOT a redirect that would confirm the route exists.
  if (me.role !== requiredRole) return { kind: 'forbidden' };

  // A buyer is `active` the moment their email is verified — `pending_approval` is the producer
  // gate only, and the producer SPA (not this one) renders the awaiting-approval banner.
  return { kind: 'allow' };
}

/** True when this user may see the buyer catalog at all. */
export function isActiveBuyer(me: Me | null | undefined): me is Me {
  return !!me && me.role === 'buyer' && me.status === 'active';
}

export function statusLabelKey(status: UserStatus): string {
  return `common:status.${status}`;
}
